import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import { FormDataType } from '@tn/formbuilder/utils/schema-types';
import type { SignInResponse } from '@tn/foundation/graphql/__generated/gql/graphql';
import { MvpdDispute, SignInFormFields } from '@tn/foundation/graphql/__generated/gql/graphql';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import { USER_LOGIN_TYPE } from '@tn/foundation/utils/constants/tracking';
import request from 'graphql-request';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    fromPromise,
    Mapper,
    OutputFrom,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { cleanFormData } from '../../utils/cleanFormData/cleanFormData';
import { forgotPasswordActor } from '../forgotPassword';
import { getErrorMessageFromEvent, getEvaluatedRoute, getUser } from '../helpers';
import { getEnabledMvpdSignInHandoff } from '../mvpd/signInHandoff';
import { getEnabledPartnerBindContext } from '../partner';
import { clearEmailFromSession } from '../ptvSisu/emailSession';
import {
    clearRendezvousSignInOnlyFlag,
    getEnabledRendezvousSignInHandoff,
} from '../rendezvous/signInHandoff';
import { shouldUseIdentityService } from '../utils/identityService';

import { identityLoginMutation, signInCacheKey, signInMutation } from './graphql/constants';
import { SIGNIN_API_CLIENT_ERROR_CODES } from './graphql/constants/errorCodes';
import { runPostSignInClientSetup } from './runPostSignInClientSetup';
import { trackSignInFailure, trackSignInSuccess } from './tracking';

export type SignInFlowContext = {
    email: string;
    password: string;
    rememberMe: number;
    userId: string;
    formBuilderRef: FormBuilderRef | null;
    formError: string | null;
    url: string;
    passIsVisible?: '0' | '1';
    code?: string;
    mvpdDispute?: MvpdDispute | null;
};

type SubmitSignInOutput = Pick<SignInFlowContext, 'userId' | 'mvpdDispute'>;
type SubmitSignInInput = Omit<SignInFlowContext, 'userId' | 'mvpdDispute'>;

/**
 * Reads the DMA-dispute lockout the machine parked on (dmaDispute state) out
 * of a signIn child snapshot. Components stay dumb: DmaDisputeModal consumes
 * the dispute through this selector instead of touching machine internals.
 * Takes unknown because child snapshots are untyped on AnyMachineSnapshot.
 */
export const selectMvpdDispute = (snapshot: unknown): MvpdDispute | null => {
    const signInSnapshot = snapshot as
        | {
              matches?: (value: string) => boolean;
              context?: { mvpdDispute?: MvpdDispute | null };
          }
        | undefined
        | null;
    return signInSnapshot?.matches?.('dmaDispute')
        ? (signInSnapshot.context?.mvpdDispute ?? null)
        : null;
};

function getUnsuccessfulSignInErrorMessage(signIn: SignInResponse | null | undefined): string {
    if (signIn?.message) {
        return signIn.message;
    }

    const firstMessage = signIn?.messages?.[0];
    if (firstMessage) {
        return firstMessage;
    }

    return SIGNIN_API_CLIENT_ERROR_CODES.DEFAULT;
}

function rejectUnsuccessfulSignIn(signIn: SignInResponse | null | undefined): never {
    const errorMessage = getUnsuccessfulSignInErrorMessage(signIn);
    throw Object.assign(new Error(errorMessage), {
        response: { errors: [{ message: errorMessage }] },
    });
}

export const submitFormSignInActor: FlowEntity = {
    initActor: ({ queryClient, getConfigByKey }) =>
        fromPromise<SubmitSignInOutput, SubmitSignInInput>(async ({ input }) => {
            const cleanedFormData = cleanFormData(input as FormDataType);
            const graphqlEndpoint = `/api/tn/signup/graphql/`;

            if (shouldUseIdentityService(queryClient)) {
                const res = await request(
                    graphqlEndpoint,
                    identityLoginMutation,
                    {
                        input: {
                            email: cleanedFormData.email as string,
                            password: cleanedFormData.password as string,
                        },
                    },
                    { 'request-source': 'identityLogin' },
                );

                const { success, userId, message, errorCode } = res.identityLogin ?? {};
                if (!success || !userId) {
                    const errorMessage = message || SIGNIN_API_CLIENT_ERROR_CODES.DEFAULT;
                    throw Object.assign(new Error(errorMessage), {
                        response: {
                            errors: [{ message: errorMessage, extensions: { code: errorCode } }],
                        },
                    });
                }

                await runPostSignInClientSetup({ queryClient, getConfigByKey });
                clearEmailFromSession();

                return { userId };
            }

            const res = await request(
                graphqlEndpoint,
                signInMutation,
                { formFields: cleanedFormData as SignInFormFields },
                { 'request-source': signInCacheKey },
            );

            if (res.signIn?.success) {
                const { mvpdDispute } = res.signIn;
                if (mvpdDispute?.isDmaInDispute && mvpdDispute?.mvpdProvider) {
                    // MVPD DMA-dispute lockout: the resolver refused the session,
                    // so there is nothing to set up client-side — surface the
                    // dispute and stop.
                    return { userId: res.signIn.userId ?? '', mvpdDispute };
                }

                await runPostSignInClientSetup({ queryClient, getConfigByKey });
                clearEmailFromSession();

                return res.signIn as SubmitSignInOutput;
            }

            rejectUnsuccessfulSignIn(res.signIn);
        }),

    inputActor: <
        Mapper<
            SignInFlowContext,
            AnyEventObject,
            Omit<
                SignInFlowContext,
                'userId' | 'mvpdDispute' | 'formBuilderRef' | 'url' | 'passIsVisible' | 'formError'
            >,
            AnyEventObject
        >
    >(({
        context: {
            userId: _userId,
            mvpdDispute: _mvpdDispute,
            formBuilderRef: _formBuilderRef,
            url: _url,
            passIsVisible: _passIsVisible,
            formError: _formError,
            ...rest
        },
    }) => {
        return { ...rest };
    }),
};

type MachineEvents =
    | { type: 'xstate.init'; input: { code?: string; url: string } }
    | {
          type: 'SUBMIT_CLICK';
          params: Omit<SignInFlowContext, 'userId' | 'mvpdDispute'>;
      }
    | {
          type: 'CONNECT_FORMBUILDER_REF';
          params: FormBuilderRef;
      }
    | { type: 'SUBMITTING' }
    | { type: 'CLEAR_FORM_ERROR' }
    | { type: 'CLICK_FORGOT_PASSWORD'; params: { email: string } }
    | { type: 'SEND_FORGOT_PASSWORD_LINK' }
    | DoneActorEvent<OutputFrom<ReturnType<typeof submitFormSignInActor.initActor>>, 'submitting'>
    | ErrorActorEvent<OutputFrom<ReturnType<typeof submitFormSignInActor.initActor>>, 'submitting'>
    | DoneActorEvent<OutputFrom<ReturnType<typeof forgotPasswordActor.initActor>>, 'forgotPassword'>
    | ErrorActorEvent<
          OutputFrom<ReturnType<typeof forgotPasswordActor.initActor>>,
          'forgotPassword'
      >;

export const signInFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router, queryClient, getConfigByKey } =
            rawParams as InitMachineParams;
        const machine = setup({
            types: {
                context: {} as SignInFlowContext,
                events: {} as MachineEvents,
                input: {} as { url: string },
            },
            guards: {
                isDmaInDispute: ({ event }) => {
                    const output = (event as DoneActorEvent<SubmitSignInOutput, 'submitting'>)
                        .output;
                    return !!(
                        output?.mvpdDispute?.isDmaInDispute && output?.mvpdDispute?.mvpdProvider
                    );
                },
            },
            actions: {
                goToSignInRoute: ({ context, event }) => {
                    assertEvent(event, 'xstate.init');
                    const evaluatedUrl = getEvaluatedRoute(router.pathname, context.url);
                    if (evaluatedUrl) {
                        router.push(context.url);
                    }
                },
                clickForgotPassword: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CLICK_FORGOT_PASSWORD');
                    enqueue.assign({ ...event.params });
                    enqueue.raise({ type: 'SEND_FORGOT_PASSWORD_LINK' });
                }),
                submitForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SUBMIT_CLICK');
                    context.formBuilderRef?.toggleLoading(true);
                    enqueue.assign({ ...event.params, formError: null });
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                saveDataFromSuccessfulSignin: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    enqueue.assign({ userId: event.output.userId });
                    // Legacy SignIn.vue clears the rendezvous sign-in-only flag
                    // right on login success, before it branches into any bind —
                    // clearing here covers every dtc continuation (plain
                    // destination, mvpdBind, partnerBind) instead of one branch.
                    clearRendezvousSignInOnlyFlag();
                    // On an MVPD or billing-partner handoff the flow continues
                    // into the dtc mvpdBind/partnerBind state: keep the loading
                    // overlay up until that either hard-redirects or drops it
                    // with the error banner/modal (handleMvpdBindError /
                    // handlePartnerBindError).
                    if (
                        !getEnabledMvpdSignInHandoff(getConfigByKey) &&
                        !getEnabledPartnerBindContext(getConfigByKey)
                    ) {
                        context.formBuilderRef?.toggleLoading(false);
                    }
                    const vendorCode =
                        getUser(queryClient)?.userFullProfile?.cbsPackageInfo?.[0]?.vendorCode;
                    trackSignInSuccess({
                        vendorCode,
                        userLoginType: getEnabledRendezvousSignInHandoff(getConfigByKey)
                            ? USER_LOGIN_TYPE.RENDEZVOUS
                            : undefined,
                    });
                }),
                handleDmaDispute: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    context.formBuilderRef?.toggleLoading(false);
                    // Legacy SignIn.vue clears the rendezvous sign-in-only flag
                    // before it even checks the dispute, so a disputed TV
                    // handoff doesn't leave the next organic sign-in in
                    // sign-in-only mode.
                    clearRendezvousSignInOnlyFlag();
                    // The machine only parks and records the dispute — the
                    // lockout modal is rendered by DmaDisputeModal, which reads
                    // it back via selectMvpdDispute (copy lives in the app
                    // layer, next to the page attributes).
                    enqueue.assign({ mvpdDispute: event.output.mvpdDispute });
                }),
                assignFormBuilderRef: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    enqueue.assign({ formBuilderRef: event.params });
                }),
                setError: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage = getErrorMessageFromEvent(event);
                    context.formBuilderRef?.toggleLoading(false);
                    enqueue.assign({ formError: errorMessage });
                }),
                clearFormError: enqueueActions(({ enqueue, context }) => {
                    if (context.formError !== null) {
                        enqueue.assign({ formError: null });
                    }
                }),
                handleError: ({ event, context }) => {
                    assertEvent(event, [
                        'xstate.error.actor.submitting',
                        'xstate.error.actor.forgotPassword',
                    ]);
                    const errorMessage = getErrorMessageFromEvent(event);
                    context.formBuilderRef?.toggleError(errorMessage);
                },
                handleErrorWithBanner: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage = getErrorMessageFromEvent(event);
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleBanner({
                        message: errorMessage,
                        variant: 'critical',
                    });
                },
                trackErrorEvent: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage = getErrorMessageFromEvent(event);
                    const errorCode = event.error.response?.errors?.[0]?.extensions?.code || '';

                    // Failure-boundary telemetry for the sign-in submit actor. Log the stable
                    // error code only — never the raw API message or email (PII).
                    appLogger.error('Sign-in submission failed', {
                        component: 'signInFlow',
                        errorCode: errorCode || 'No code provided',
                    });

                    trackSignInFailure({
                        passIsVisible: context.passIsVisible,
                        appLogText: errorMessage,
                        appLogCode: errorCode,
                    });
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                email: '',
                password: '',
                rememberMe: 1,
                userId: '',
                formBuilderRef: null,
                formError: null,
                passIsVisible: '0',
                ...input,
            }),
        });

        return machine;
    },
    outputActor: <Mapper<SignInFlowContext, AnyEventObject, { userId: string }, AnyEventObject>>(
        (({ context }) => {
            return { ...context };
        })
    ),
};
